import type { ReactNode, Ref } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import { Spinner } from '@/components/Spinner'
import { Tooltip } from '@/components/Tooltip'
import {
  LegendStatusIcon,
  VersionChip,
} from '@/features/legends/components/LegendStatus'
import type { LegendStatus } from '@/features/legends/legend-status'
import styles from './VersionPicker.module.css'

export interface VersionPickerProps {
  /**
   * `VersionPickerItem`s, then the infinite scroll trigger.
   */
  children?: ReactNode
  testId?: string
}

/**
 * The legend's versions, latest first.
 */
export function VersionPicker({ children, testId }: VersionPickerProps) {
  return (
    <div className={styles.root} data-test={testId}>
      <div className={styles.header}>Version history</div>
      <div className={styles.list}>{children}</div>
    </div>
  )
}

export interface VersionPickerItemProps {
  version: number
  status: LegendStatus
  notes: string
  /**
   * When the version was published, or last changed for a draft.
   */
  date: string
  /**
   * `date` as a full date and time, shown on hover.
   */
  fullDate: string
  updatedByName: string
  isSelected: boolean
  isPending: boolean
  isDisabled?: boolean
  onPress: () => unknown
  testId?: string
}

/**
 * A version in the version picker.
 */
export function VersionPickerItem({
  version,
  status,
  notes,
  date,
  fullDate,
  updatedByName,
  isSelected,
  isPending,
  isDisabled,
  onPress,
  testId,
}: VersionPickerItemProps) {
  return (
    <RawButton
      className={cx(styles.item, isSelected && styles.selected)}
      isPending={isPending}
      isDisabled={isDisabled}
      onPress={onPress}
      testId={testId}
    >
      <VersionChip
        version={version}
        status={status}
        isDraft={status === 'Draft'}
      />
      <span className={styles.info}>
        <span className={styles.notes} title={notes}>
          {notes || 'Unpublished changes'}
        </span>
        <Tooltip content={fullDate}>
          <span className={styles.subtitle}>
            <LegendStatusIcon status={status} />
            {date}
            {updatedByName && <> by {updatedByName}</>}
          </span>
        </Tooltip>
      </span>
      {isPending && <Spinner size={14} />}
    </RawButton>
  )
}

/**
 * A placeholder for a {@link VersionPickerItem} still loading. Render it inside
 * a `SkeletonList` or `SkeletonListInfiniteScrollTrigger`.
 */
export function VersionPickerItemSkeleton() {
  const { vary, ref } = useSkeletonListContext()
  return (
    <div
      ref={ref as Ref<HTMLDivElement> | undefined}
      className={cx(styles.item, styles.skeleton)}
      aria-hidden
    >
      <Skeleton block variant="rect" width={28} height={20} />
      <span className={styles.info}>
        <span className={styles.notes}>
          <Skeleton width={vary(150)} />
        </span>
        <span className={styles.subtitle}>
          <Skeleton width={vary(180)} />
        </span>
      </span>
    </div>
  )
}
